import { autoinject } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { saveAs } from 'file-saver';
import pptxgen from 'pptxgenjs';
import { HttpClient, json } from 'aurelia-fetch-client';

const eldiLogo = require('../images/eldi_logo_light.png');

export interface TableData {
  title: string;
  headers: string[];
  rows: (string | number)[][];
}

export interface ChartSeriesData {
  name: string;
  values: number[];
}

export interface ChartData {
  title: string;
  categories: string[];
  series: ChartSeriesData[];
}

const ELDI_COLORS = {
  oceanBlack: '06232d',
  darkTeal: '003e52',
  mediumTeal: '0e4959',
  teal: '437c8d',
  lightTeal: 'a8c6ce',
  white: 'ffffff',
};

const CHART_COLORS = ['0e4959', '437c8d', 'a8c6ce', '003e52', '06232d', '5a9bab', '2d6b7a', '7db5c4'];

@autoinject
export class StatisticsExportService {
  private logoData: string = eldiLogo;

  constructor(
    private t: I18N,
    private http: HttpClient
  ) {}

  private async getLogoBase64(): Promise<string | null> {
    if (this.logoData.startsWith('data:')) {
      return this.logoData;
    }
    try {
      const response = await fetch(this.logoData);
      const blob = await response.blob();
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
      });
    } catch {
      return null;
    }
  }

  public async exportToExcel(tables: TableData[], filename: string): Promise<void> {
    const requestData = {
      filename: filename,
      tables: tables.map((table) => ({
        title: table.title,
        headers: table.headers,
        rows: table.rows.map((row) => row.map((cell) => String(cell))),
      })),
    };

    const response = await this.http.fetch('/api/statistics-dashboard/export-service-data-excel', {
      method: 'POST',
      body: json(requestData),
    });

    if (!response.ok) {
      throw new Error('Failed to export Excel file');
    }

    const blob = await response.blob();
    saveAs(blob, `${filename}.xlsx`);
  }

  public async exportToPowerPoint(
    tables: TableData[],
    _templateId: string,
    customTitle?: string,
    dateRange?: { from: Date; to: Date },
    charts?: ChartData[]
  ): Promise<void> {
    const logoBase64 = await this.getLogoBase64();

    const pres = new pptxgen();

    pres.author = 'Eldi Notlogg';
    pres.title = customTitle || this.t.tr('statistics.dashboard') || 'Statistics Dashboard';
    pres.subject = 'Service Statistics Report';
    pres.company = 'Eldi';

    pres.defineSlideMaster({
      title: 'ELDI_MASTER',
      background: { color: ELDI_COLORS.white },
      objects: [
        // Header
        {
          rect: {
            x: 0,
            y: 0,
            w: '100%',
            h: 0.6,
            fill: { color: ELDI_COLORS.darkTeal },
          },
        },
        // Footer
        {
          rect: {
            x: 0,
            y: 5.2,
            w: '100%',
            h: 0.3,
            fill: { color: ELDI_COLORS.mediumTeal },
          },
        },
      ],
    });

    this.createPresentation(pres, tables, customTitle, dateRange, charts, logoBase64);

    const fileName = `${customTitle || 'Statistics_Report'}_${this.formatDate(new Date())}.pptx`;
    const blob = (await pres.write({ outputType: 'blob' })) as Blob;
    saveAs(blob, fileName);
  }

  private createPresentation(
    pres: pptxgen,
    tables: TableData[],
    customTitle?: string,
    dateRange?: { from: Date; to: Date },
    charts?: ChartData[],
    logoBase64?: string | null
  ): void {
    this.addTitleSlide(pres, customTitle, dateRange, logoBase64);

    tables.forEach((table) => {
      this.addTableSlide(pres, table, table.title, logoBase64);
    });

    if (charts && charts.length > 0) {
      charts.forEach((chart) => {
        this.addChartSlide(pres, chart, logoBase64);
      });
    }
  }

  private addChartSlide(pres: pptxgen, chart: ChartData, logoBase64?: string | null): void {
    const slide = pres.addSlide({ masterName: 'ELDI_MASTER' });

    if (logoBase64) {
      slide.addImage({
        data: logoBase64,
        x: 0.5,
        y: 0.1,
        h: 0.4,
        w: 1.2,
      });
    }

    slide.addText(chart.title, {
      x: 2,
      y: 0.15,
      w: 7,
      h: 0.4,
      fontSize: 16,
      bold: true,
      color: ELDI_COLORS.white,
      fontFace: 'Arial',
    });

    const chartData = chart.series.map((s) => ({
      name: s.name,
      labels: chart.categories,
      values: s.values,
    }));

    slide.addChart(pres.ChartType.bar, chartData, {
      x: 0.5,
      y: 0.8,
      w: 9,
      h: 4,
      barDir: 'col',
      barGrouping: 'clustered',
      showLegend: true,
      legendPos: 'b',
      showTitle: false,
      chartColors: CHART_COLORS.slice(0, chart.series.length),
      valAxisMaxVal: undefined,
      catAxisTitle: '',
      valAxisTitle: '',
    });
  }

  private addTitleSlide(pres: pptxgen, customTitle?: string, dateRange?: { from: Date; to: Date }, logoBase64?: string | null): void {
    const slide = pres.addSlide({ masterName: 'ELDI_MASTER' });

    if (logoBase64) {
      slide.addImage({
        data: logoBase64,
        x: 0.5,
        y: 0.1,
        h: 0.4,
        w: 1.2,
      });
    }

    // Title
    slide.addText(customTitle || this.t.tr('reports.servicestatistics.title') || 'Service Statistics', {
      x: 0.5,
      y: 2,
      w: 9,
      h: 1,
      fontSize: 36,
      bold: true,
      color: ELDI_COLORS.darkTeal,
      fontFace: 'Arial',
    });

    // Date range
    if (dateRange) {
      slide.addText(`${this.formatDate(dateRange.from)} - ${this.formatDate(dateRange.to)}`, {
        x: 0.5,
        y: 3,
        w: 9,
        h: 0.5,
        fontSize: 18,
        color: ELDI_COLORS.teal,
        fontFace: 'Arial',
      });
    }

    // Generated date
    slide.addText(`${this.t.tr('general.generated') || 'Generated'}: ${this.formatDate(new Date())}`, {
      x: 0.5,
      y: 5.25,
      w: 4,
      h: 0.2,
      fontSize: 10,
      color: ELDI_COLORS.white,
      fontFace: 'Arial',
    });
  }

  // Max 10 rows per slide - large tables are split with page indicators
  private readonly MAX_ROWS_PER_SLIDE = 10;

  private addTableSlide(pres: pptxgen, table: TableData, slideTitle: string, logoBase64?: string | null): void {
    if (!table.headers || table.headers.length === 0 || !table.rows || table.rows.length === 0) {
      return;
    }

    const totalRows = table.rows.length;
    const totalPages = Math.ceil(totalRows / this.MAX_ROWS_PER_SLIDE);

    // Split rows into pages
    for (let page = 0; page < totalPages; page++) {
      const startIdx = page * this.MAX_ROWS_PER_SLIDE;
      const endIdx = Math.min(startIdx + this.MAX_ROWS_PER_SLIDE, totalRows);
      const pageRows = table.rows.slice(startIdx, endIdx);

      // Add page indicator to title if multiple pages
      const pageTitle = totalPages > 1 ? `${slideTitle} (${page + 1}/${totalPages})` : slideTitle;

      this.addSingleTableSlide(pres, table.headers, pageRows, pageTitle, logoBase64, page === totalPages - 1);
    }
  }

  private addSingleTableSlide(
    pres: pptxgen,
    headers: string[],
    rows: (string | number)[][],
    slideTitle: string,
    logoBase64?: string | null,
    isLastPage: boolean = true
  ): void {
    const slide = pres.addSlide({ masterName: 'ELDI_MASTER' });

    // Add logo
    if (logoBase64) {
      slide.addImage({
        data: logoBase64,
        x: 0.5,
        y: 0.1,
        h: 0.4,
        w: 1.2,
      });
    }

    // Slide title in header
    slide.addText(slideTitle, {
      x: 2,
      y: 0.15,
      w: 7,
      h: 0.4,
      fontSize: 16,
      bold: true,
      color: ELDI_COLORS.white,
      fontFace: 'Arial',
    });

    // Build table data for pptxgenjs
    const tableRows: pptxgen.TableRow[] = [];

    // Header
    const headerRow: pptxgen.TableCell[] = headers.map((header) => ({
      text: header,
      options: {
        fill: { color: ELDI_COLORS.mediumTeal },
        color: ELDI_COLORS.white,
        bold: true,
        fontSize: 11,
        fontFace: 'Arial',
        align: 'center',
        valign: 'middle',
      },
    }));
    tableRows.push(headerRow);

    // Data
    rows.forEach((row, rowIndex) => {
      // Only highlight last row if this is the last page (summary row styling)
      const isLastRowOfTable = isLastPage && rowIndex === rows.length - 1;
      const dataRow: pptxgen.TableCell[] = row.map((cell, cellIndex) => ({
        text: String(cell),
        options: {
          fill: { color: isLastRowOfTable ? ELDI_COLORS.lightTeal : rowIndex % 2 === 0 ? 'f5f5f5' : ELDI_COLORS.white },
          color: ELDI_COLORS.oceanBlack,
          bold: isLastRowOfTable || cellIndex === 0,
          fontSize: 10,
          fontFace: 'Arial',
          align: cellIndex === 0 ? 'left' : 'center',
          valign: 'middle',
        },
      }));
      tableRows.push(dataRow);
    });

    // Add table to slide
    const colCount = headers.length;
    const tableWidth = Math.min(9, colCount * 1.8);

    slide.addTable(tableRows, {
      x: 0.5,
      y: 0.8,
      w: tableWidth,
      colW: tableWidth / colCount,
      rowH: 0.35,
      border: { type: 'solid', pt: 0.5, color: ELDI_COLORS.teal },
    });
  }

  private formatDate(date: Date): string {
    return date.toLocaleDateString('nb-NO', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  }
}
